import { useMemo } from 'react';
import { SxProps } from '@mui/system';
import { ReactComponent as ConfigIcon } from '../../assets/20x20/config.svg';
import useUserPreferences from '../../hooks/useUserPreferences';
import IconButton from '../IconButton';
import Menu from '../Menu';
import { Column } from './types';

type Props<T> = {
  sx?: SxProps;
  columns: Column<T>[];
  localStorageKey: string;
  onChange?: (columns: string[]) => void;
};

const ToggleColumnsMenu = <T extends object>({
  sx,
  columns,
  localStorageKey,
  onChange,
}: Props<T>) => {
  const { userPreferences, updateUserPreferences } = useUserPreferences();

  const toggleableColumns = useMemo(() => {
    const filteredColumns = columns.filter(
      (c) => !c.nonToggleable && !c.hideColumn,
    );

    // Apply saved column order if available
    const savedOrder = userPreferences.columnOrder?.[localStorageKey];
    if (savedOrder && savedOrder.length > 0) {
      const orderedColumns: Column<T>[] = [];
      const remainingColumns = [...filteredColumns];

      // First, add columns in the saved order
      savedOrder.forEach((colId) => {
        const colIndex = remainingColumns.findIndex((c) => c.id === colId);
        if (colIndex !== -1) {
          orderedColumns.push(remainingColumns[colIndex]);
          remainingColumns.splice(colIndex, 1);
        }
      });

      // Then, add any remaining columns that weren't in the saved order
      orderedColumns.push(...remainingColumns);

      return orderedColumns;
    }

    return filteredColumns;
  }, [columns, userPreferences.columnOrder, localStorageKey]);

  const selectedColumns = useMemo(() => {
    const storageColumns = userPreferences.visibleColumns?.[localStorageKey];
    return (
      storageColumns ||
      toggleableColumns.filter((c) => !c.defaultUntoggled).map((c) => c.id)
    );
  }, [userPreferences.visibleColumns, localStorageKey, toggleableColumns]);

  const handleChange = (newColumnKeys: string[]) => {
    updateUserPreferences('visibleColumns', {
      ...(userPreferences.visibleColumns || {}),
      [localStorageKey]: newColumnKeys,
    });
    onChange?.(newColumnKeys);
  };

  return (
    <Menu
      menuButton={
        <IconButton sx={{ p: 0.5, ...sx }} aria-label="Toggle columns">
          <ConfigIcon />
        </IconButton>
      }
      options={toggleableColumns.map((c) => ({ label: c.header, value: c.id }))}
      selectedOptions={selectedColumns}
      onChange={handleChange}
      multiple
    />
  );
};

export default ToggleColumnsMenu;
